// @ts-nocheck
import { twitchApi } from '../services/api-client';
import { configureTwitchEmbedParents, createTwitchChatUrl, warnAboutUnsupportedEmbedOrigin } from '../services/twitch-embed';
import { t } from '../services/i18n';
import { GlobalChat } from '../features/chat/global-chat';
import { StreamCombos } from '../features/combos/stream-combos';
import { LatencyManager } from '../features/player/latency-manager';
import { PlayerManager } from '../features/player/player-manager';
import { Sidebar } from '../features/sidebar/sidebar';
import { WelcomeScreen } from '../features/welcome/welcome-screen';

// Application coordinator. Feature implementations live in dedicated modules.
class MultiStreamApp {
    constructor() {
        // Cache DOM references
        this.sidebar = new Sidebar();
        this.playerManager = new PlayerManager(streamerId => this.removeStreamer(streamerId));
        this.latencyManager = new LatencyManager(this.playerManager);
        this.streamersList = document.getElementById('streamer-list');
        this.streamerSearch = document.getElementById('streamer-search');
        this.addStreamerBtn = document.getElementById('add-streamer');
        this.chatContainer = document.getElementById('chat-container');
        this.gridLayoutSelect = document.getElementById('grid-layout');
        
        // Initialiser le chat global
        this.globalChat = new GlobalChat(this.chatContainer);
        
        // Use Map for O(1) lookups
        this.streamers = new Map(); // Map of streamer data by ID
        this.streamersOrder = []; // Pour maintenir l'ordre des streamers
        window.addEventListener('interface-language-change', () => this.refreshTranslatedInterface());
        
        // Event delegation for better performance
        this.streamersList.addEventListener('click', this.handleStreamerListClick.bind(this));
        
        // Définir la fonction de réorganisation explicitement
        this.handleStreamersReorder = (newOrder) => {
            // Mettre à jour l'ordre des streamers
            this.streamersOrder = newOrder;
            this.syncStreamerSections();
            
            // Utiliser l'ordre final (après regroupement live/file d'attente)
            // comme source unique pour la grille et l'URL partageable.
            this.playerManager.reorderPlayers(this.streamersOrder);
            this.updateUrl();
            
            // Notification subtle pour informer l'utilisateur
            this.showNotification(t('stream.reordered'), 'info', 2000);
        };
        
        // Initialiser le drag and drop
        this.sidebar.initDragAndDrop(this.streamersList, this.handleStreamersReorder);
        
        // Initialiser le déclencheur pour les mini-streamers de la sidebar
        this.sidebar.initMiniStreamers({
            openChat: (streamerId) => {
                const streamer = this.streamers.get(streamerId);
                if (streamer) {
                    this.openChat(streamer.login);
                    this.sidebar.activateTab('chat');
                }
            },
            getVolumeState: (streamerId) => this.playerManager.getPlayerVolumeState(streamerId),
            setVolume: (streamerId, volume) => this.playerManager.setPlayerVolume(streamerId, volume),
            toggleMuted: (streamerId) => this.playerManager.togglePlayerMuted(streamerId),
            maximize: (streamerId) => this.playerManager.maximizedManager.maximizePlayer(streamerId),
            focus: (streamerId) => this.playerManager.zoomedManager.zoomPlayer(streamerId),
            removeStreamer: (streamerId) => {
                this.removeStreamer(streamerId);
            }
        });
        
        // Initialize
        this.init();
        
        // Notification system
        this.notificationQueue = [];
        this.notificationActive = false;
        
        // Initialiser l'écran d'accueil
        this.welcomeScreen = new WelcomeScreen();
        
        // Initialiser le gestionnaire de combos
        this.streamCombos = new StreamCombos();
        
        // Exposer les méthodes clés pour la sidebar minimisée
        window.app = {
            openStreamerChat: (streamerId) => {
                const streamer = this.streamers.get(streamerId);
                if (streamer) {
                    this.openChat(streamer.login);
                }
            },
            removePlayer: (streamerId) => {
                this.removeStreamer(streamerId);
            },
            openGlobalChat: () => {
                this.openGlobalChat();
            },
            showDefaultChatMessage: () => {
                this.showDefaultChatMessage();
            }
        };
    }

    async init() {
        warnAboutUnsupportedEmbedOrigin();

        // Initialize event listeners
        this.addStreamerBtn.addEventListener('click', () => this.addStreamer());
        this.streamerSearch.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                this.addStreamer();
            }
        });
        
        this.gridLayoutSelect.addEventListener('change', (e) => {
            this.playerManager.updateLayout(e.target.value);
        });
        
        // Initialiser les contrôles de chat dans la sidebar (active le mode global par défaut)
        this.sidebar.initChatControls();
        
        // Afficher le message par défaut du chat au démarrage
        this.showDefaultChatMessage();
        
        // Show loading state
        this.showLoadingIndicator();
        
        try {
            const embedConfiguration = twitchApi.getConfig()
                .then(({ twitch }) => configureTwitchEmbedParents(twitch.embedParents))
                .catch((error) => {
                    console.warn('[Twitch embed] Configuration serveur indisponible, utilisation du domaine courant.', error);
                });

            // Get session data from backend API
            const response = await fetch('/api/session/load', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    path: window.location.pathname
                })
            });
            
            if (!response.ok) {
                throw new Error('Failed to load session data');
            }

            await embedConfiguration;
            
            const sessionData = await response.json();
            
            if (sessionData.streamers && sessionData.streamers.length > 0) {
                await this.renderStreamers(sessionData.streamers);
            } else {
                this.showWelcomeMessage();
            }
        } catch (error) {
            console.error('Error initializing app:', error);
            this.showWelcomeMessage();
            this.showNotification(t('stream.loadError'), 'error');
        }
        
        // Set up refresh interval
        setInterval(() => this.refreshStreamersData(), 60000);
    }
    
    showLoadingIndicator() {
        this.streamersList.innerHTML = `<div class="loading"><div class="spinner"></div>${t('common.loading')}</div>`;
    }

    // Handle clicks on streamer list items
    handleStreamerListClick(e) {
        const target = e.target;
        const chatBtn = target.closest('.chat-btn');
        const removeBtn = target.closest('.remove-btn');
        const latencyBtn = target.closest('.latency-btn');
        const focusBtn = target.closest('.focus-btn');
        const maximizeBtn = target.closest('.maximize-btn');
        
        if (!chatBtn && !removeBtn && !latencyBtn && !focusBtn && !maximizeBtn) return;
        
        const listItem = target.closest('.streamer-item');
        if (!listItem) return;
        
        const streamerId = listItem.dataset.streamerId;
        
        if (chatBtn) {
            const streamer = this.streamers.get(streamerId);
            if (streamer) {
                this.openChat(streamer.login);
                this.sidebar.activateTab('chat');
            }
        } else if (removeBtn) {
            this.removeStreamer(streamerId);
        } else if (latencyBtn) {
            const streamer = this.streamers.get(streamerId);
            if (streamer) {
                this.latencyManager.showLatencyModal(streamerId, streamer.display_name);
            }
        } else if (focusBtn) {
            this.playerManager.zoomedManager.zoomPlayer(streamerId);
        } else if (maximizeBtn) {
            this.playerManager.maximizedManager.maximizePlayer(streamerId);
        }
    }

    // Add a welcome message method to guide new users
    showWelcomeMessage() {
        const origin = window.location.origin;
        
        this.streamersList.innerHTML = `
            <div class="welcome-message">
                <h3>${t('welcome.title')}</h3>
                <p>${t('welcome.sidebarIntro')}</p>
                <p>${t('welcome.sidebarShare')}</p>
                <p>${t('welcome.examples')}</p>
                <ul>
                    <li>${origin}/xqc</li>
                    <li>${origin}/shroud/pokimane</li>
                    <li>${origin}/ninja/tfue/myth</li>
                </ul>
            </div>
        `;
    }

    // Add streamer using backend API
    async addStreamer() {
        const username = this.streamerSearch.value.trim();
        if (!username) return;
        
        // Create and show loading indicator
        const searchButton = this.addStreamerBtn;
        const originalButtonContent = searchButton.innerHTML;
        searchButton.innerHTML = '<i class="fas fa-spinner fa-spin"></i>';
        searchButton.disabled = true;
        this.streamerSearch.disabled = true;
        
        try {
            // Check if already exists
            const existingStreamer = Array.from(this.streamers.values()).find(s => s.login.toLowerCase() === username.toLowerCase());
            if (existingStreamer) {
                this.showNotification(t('stream.alreadyAdded', { name: username }), 'info');
                return;
            }
            
            // Call backend API to add streamer
            const response = await fetch('/api/streamers/add', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ username })
            });
            
            const result = await response.json();
            
            if (!result.success) {
                this.showNotification(result.error || t('stream.addError'), 'error');
                return;
            }
            
            let streamer = result.streamer;

            // Compatibilité avec un backend encore actif sur l'ancien contrat :
            // il signalait les chaînes hors ligne sans retourner leur profil.
            if (!streamer && result.added === false) {
                streamer = await this.resolveQueuedStreamer(username);
            }

            if (!streamer?.id || !streamer?.login) {
                this.showNotification(result.message || t('stream.retrieveError'), 'error');
                return;
            }
            
            // Clear search input
            this.streamerSearch.value = '';
            if (this.sidebar.searchResults) {
                this.sidebar.setSearchResultsVisible(false);
            }
            
            // Add to our local data
            this.streamers.set(streamer.id, streamer);
            
            // First, clear welcome message if it's still displayed
            if (this.isWelcomeMessageDisplayed()) {
                this.streamersList.innerHTML = '';
            }
            this.streamersOrder.push(streamer.id);
            
            // Add to DOM
            const item = this.createStreamerListItem(streamer);
            this.streamersList.appendChild(item);
            this.syncStreamerSections();
            
            // Add player if live
            if (streamer.is_live) {
                this.playerManager.addPlayer(
                    streamer.id,
                    streamer.login,
                    streamer.display_name,
                    streamer.profile_image_url,
                );
            }
            
            // Update URL
            this.updateUrl();
            
            // Mettre à jour le chat global
            const activeStreamers = Array.from(this.streamers.values()).filter(s => s.is_live);
            if (this.globalChat.isGlobalMode && activeStreamers.length > 0) {
                // Si le chat global est déjà actif, mettre à jour la liste des streamers
                await this.globalChat.updateActiveStreamers(activeStreamers);
            } else {
                // Sinon, utiliser la logique par défaut
                this.showDefaultChatMessage();
            }
            
            // Show success notification
            this.showNotification(
                streamer.is_live
                    ? t('stream.added', { name: streamer.display_name })
                    : t('stream.queued', { name: streamer.display_name }),
                streamer.is_live ? 'success' : 'info',
            );
            
        } catch (error) {
            console.error('Error adding streamer:', error);
            this.showNotification(t('stream.addError'), 'error');
        } finally {
            // Restore button state
            searchButton.innerHTML = originalButtonContent;
            searchButton.disabled = false;
            this.streamerSearch.disabled = false;
            this.streamerSearch.focus({ preventScroll: true });
        }
    }

    async resolveQueuedStreamer(username) {
        const params = new URLSearchParams({ login: username });
        const response = await fetch(`/api/twitch/users?${params.toString()}`);
        if (!response.ok) return null;

        const payload = await response.json();
        const user = Array.isArray(payload.data) ? payload.data[0] : null;
        if (!user) return null;

        return {
            id: user.id,
            login: user.login,
            display_name: user.display_name,
            profile_image_url: user.profile_image_url || '',
            is_live: false,
        };
    }

    // Create a DOM element for a streamer
    formatViewerCount(viewerCount) {
        return new Intl.NumberFormat(document.documentElement.lang || navigator.language, {
            notation: 'compact',
            maximumFractionDigits: 1,
        }).format(Number(viewerCount) || 0);
    }

    createViewerBadge(viewerCount) {
        const badge = document.createElement('div');
        const exactCount = new Intl.NumberFormat(document.documentElement.lang || navigator.language).format(Number(viewerCount) || 0);
        badge.className = 'streamer-viewers';
        badge.title = t('stream.spectators', { count: exactCount });
        badge.setAttribute('aria-label', badge.title);

        const liveDot = document.createElement('span');
        liveDot.className = 'viewer-live-dot';
        liveDot.setAttribute('aria-hidden', 'true');

        const icon = document.createElement('i');
        icon.className = 'fas fa-user';
        icon.setAttribute('aria-hidden', 'true');

        const count = document.createElement('span');
        count.className = 'viewer-count';
        count.textContent = this.formatViewerCount(viewerCount);
        badge.append(liveDot, icon, count);
        return badge;
    }

    createStreamerListItem(streamer) {
        const item = document.createElement('li');
        item.className = `streamer-item sidebar-general ${streamer.is_live ? 'live' : 'queued'}`;
        item.dataset.streamerId = streamer.id;
        item.draggable = true; // Rendre l'élément draggable
        
        const gameInfo = streamer.is_live ? streamer.game_name : t('stream.waiting');
        const playerControlDisabled = streamer.is_live ? '' : ' disabled';
        
        item.innerHTML = `
            <img class="streamer-avatar" src="${streamer.profile_image_url}" alt="${streamer.display_name}">
            <div class="streamer-info">
                <div class="streamer-name">${streamer.display_name}</div>
                <div class="streamer-game">${gameInfo}</div>
            </div>
            <div class="streamer-end">
                <div class="streamer-viewer-slot"></div>
                <div class="streamer-actions">
                    <button class="chat-btn" title="${t('stream.chat')}" aria-label="${t('stream.chat')}"><i class="fas fa-comments"></i></button>
                    <button class="focus-btn player-action-btn" title="${t('stream.focus')}" aria-label="${t('stream.focus')}"${playerControlDisabled}><i class="fas fa-search-plus"></i></button>
                    <button class="maximize-btn player-action-btn" title="${t('stream.maximize')}" aria-label="${t('stream.maximize')}"${playerControlDisabled}><i class="fas fa-expand"></i></button>
                    <span class="streamer-action-separator" aria-hidden="true"></span>
                    <button class="latency-btn player-action-btn" title="${t('stream.latency')}" aria-label="${t('stream.latency')}" data-streamer-id="${streamer.id}" data-streamer-name="${streamer.display_name}"${playerControlDisabled}><i class="fas fa-clock"></i></button>
                    <button class="remove-btn" title="${t('common.remove')}" aria-label="${t('common.remove')}"><i class="fas fa-trash"></i></button>
                </div>
            </div>
        `;

        if (streamer.is_live) {
            item.querySelector('.streamer-viewer-slot')?.append(this.createViewerBadge(streamer.viewer_count));
        }
        
        // Update latency button appearance if latency is set
        setTimeout(() => {
            this.updateLatencyButtonAppearance(streamer.id);
        }, 0);
        
        return item;
    }

    createQueueDivider(count) {
        const divider = document.createElement('li');
        divider.className = 'streamer-queue-divider';
        divider.setAttribute('role', 'separator');
        divider.innerHTML = '<span class="queue-divider-line"></span><span class="queue-divider-label"><i class="fas fa-clock" aria-hidden="true"></i><span></span></span><span class="queue-divider-line"></span>';
        divider.querySelector('.queue-divider-label span').textContent = t('stream.queue', { count });
        return divider;
    }

    syncStreamerSections() {
        if (!this.streamersList || this.streamers.size === 0) return;

        const knownIds = this.streamersOrder.filter(id => this.streamers.has(id));
        this.streamers.forEach((_streamer, id) => {
            if (!knownIds.includes(id)) knownIds.push(id);
        });
        const liveIds = knownIds.filter(id => this.streamers.get(id)?.is_live);
        const queuedIds = knownIds.filter(id => !this.streamers.get(id)?.is_live);
        this.streamersOrder = [...liveIds, ...queuedIds];

        this.streamersList.querySelector('.streamer-queue-divider')?.remove();
        liveIds.forEach(id => {
            const item = this.streamersList.querySelector(`.streamer-item[data-streamer-id="${id}"]`);
            if (item) this.streamersList.appendChild(item);
        });
        if (queuedIds.length > 0) {
            this.streamersList.appendChild(this.createQueueDivider(queuedIds.length));
        }
        queuedIds.forEach(id => {
            const item = this.streamersList.querySelector(`.streamer-item[data-streamer-id="${id}"]`);
            if (item) this.streamersList.appendChild(item);
        });
    }

    // Display streamers from data
    async renderStreamers(streamers) {
        if (!streamers || streamers.length === 0) {
            this.showWelcomeMessage();
            return;
        }
        
        // Clear any existing welcome message
        if (this.isWelcomeMessageDisplayed()) {
            this.streamersList.innerHTML = '';
        }
        
        // Create a document fragment to batch DOM operations
        const fragment = document.createDocumentFragment();
        const playersToAdd = [];
        const playersToRemove = [];
        
        // Get current streamer IDs to compare
        const currentStreamerIds = new Set(this.streamers.keys());
        const newStreamerIds = new Set(streamers.map(s => s.id));
        
        // Find streamers to remove (exist in current but not in new)
        currentStreamerIds.forEach(id => {
            if (!newStreamerIds.has(id)) {
                playersToRemove.push(id);
            }
        });
        
        // Initialiser streamersOrder
        this.streamersOrder = [];
        
        // Process all streamers
        streamers.forEach(streamer => {
            // Store in our local data structure
            this.streamers.set(streamer.id, streamer);
            this.streamersOrder.push(streamer.id);
            
            // Create list item
            const item = this.createStreamerListItem(streamer);
            fragment.appendChild(item);
            
            // Queue for player creation if live and not already existing
            if (streamer.is_live) {
                playersToAdd.push({
                    id: streamer.id,
                    login: streamer.login,
                    displayName: streamer.display_name,
                    profileImageUrl: streamer.profile_image_url,
                });
            } else {
                // If streamer went offline, remove their player
                if (this.playerManager.players.has(streamer.id)) {
                    playersToRemove.push(streamer.id);
                }
            }
        });
        
        // Remove players for streamers that are no longer present or offline
        playersToRemove.forEach(streamerId => {
            this.playerManager.removePlayer(streamerId);
        });
        
        // Clear existing list and add all new items at once
        this.streamersList.innerHTML = '';
        this.streamersList.appendChild(fragment);
        this.syncStreamerSections();
        
        // Add/update all players in parallel for immediate individual loading
        requestAnimationFrame(() => {
            // Add all players immediately in parallel
            playersToAdd.forEach(player => {
                // addPlayer will handle existing players automatically
                this.playerManager.addPlayer(
                    player.id,
                    player.login,
                    player.displayName,
                    player.profileImageUrl,
                );
            });
            
            // Activer le chat global si des streamers sont en live
            this.showDefaultChatMessage();
        });
    }

    // Remove a streamer - communicates with backend
    async removeStreamer(streamerId) {
        if (!this.streamers.has(streamerId)) return;
        
        const streamer = this.streamers.get(streamerId);
        
        try {
            // Call backend to update session
            const response = await fetch('/api/streamers/remove', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ streamerId })
            });
            
            if (!response.ok) {
                console.error('Error removing streamer from backend');
            }
            
            // Remove from our data
            this.streamers.delete(streamerId);
            this.streamersOrder = this.streamersOrder.filter(id => id !== streamerId);
            
            // Remove from DOM with animation
            const item = document.querySelector(`.streamer-item[data-streamer-id="${streamerId}"]`);
            if (item) {
                item.classList.add('fade-out');
                setTimeout(() => {
                    if (item.parentNode) {
                        item.parentNode.removeChild(item);
                    }
                    this.syncStreamerSections();
                }, 300); // Match animation duration
            }
            
            // Remove player
            this.playerManager.removePlayer(streamerId);
            
            // Update URL
            this.updateUrl();
            
            // Mettre à jour le chat global si actif
            if (this.globalChat.isGlobalMode) {
                const activeStreamers = Array.from(this.streamers.values()).filter(streamer => streamer.is_live);
                await this.globalChat.updateActiveStreamers(activeStreamers);
            }
            
            // Check if there are no more streamers and show welcome message if needed
            if (this.streamers.size === 0) {
                this.showWelcomeMessage();
            }
            
            this.showNotification(t('stream.removed', { name: streamer.display_name }), 'info');
        } catch (error) {
            console.error('Error removing streamer:', error);
            this.showNotification(t('stream.removeError'), 'error');
        }
    }

    // Open Twitch chat for a streamer
    openChat(streamerName) {
        if (!streamerName) {
            this.showDefaultChatMessage();
            return;
        }

        // Une action ciblant un streamer doit toujours sélectionner le mode individuel.
        this.sidebar.activateChatMode('individual');
        
        // Désactiver le mode chat global si actif
        this.globalChat.disableGlobalChat();
        
        // Vider le conteneur et supprimer toutes les classes
        this.chatContainer.innerHTML = '';
        this.chatContainer.className = '';
        
        // Créer directement l'iframe du chat Twitch sans header ni contrôles
        const iframe = document.createElement('iframe');
        iframe.className = 'chat-embed';
        iframe.src = createTwitchChatUrl(streamerName);
        
        this.chatContainer.appendChild(iframe);
    }

    // Activer le chat global
    openGlobalChat() {
        const activeStreamers = Array.from(this.streamers.values()).filter(streamer => streamer.is_live);
        
        if (activeStreamers.length === 0) {
            this.showNotification(t('chat.noLiveForGlobal'), 'warning');
            return;
        }
        
        this.globalChat.enableGlobalChat(activeStreamers);
        this.sidebar.activateTab('chat');
    }

    // Refresh streamers data from backend
    async refreshStreamersData() {
        if (this.streamers.size === 0) return;
        
        try {
            // Get all current streamer IDs
            const streamerIds = Array.from(this.streamers.keys());
            
            // Call backend to get fresh data
            const response = await fetch('/api/streamers/refresh', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ streamerIds })
            });
            
            if (!response.ok) {
                throw new Error('Failed to refresh streamers');
            }
            
            const result = await response.json();
            
            if (!result.streamers) {
                return;
            }
            
            // Track status changes
            const statusChanges = { wentLive: [], wentOffline: [] };
            
            // Process updates
            result.streamers.forEach(updatedStreamer => {
                const currentStreamer = this.streamers.get(updatedStreamer.id);
                
                // Skip if streamer no longer in our list
                if (!currentStreamer) return;
                
                // Check if live status changed
                const wasLive = currentStreamer.is_live;
                const isNowLive = updatedStreamer.is_live;
                
                if (wasLive !== isNowLive) {
                    if (isNowLive) {
                        statusChanges.wentLive.push({
                            id: updatedStreamer.id,
                            name: updatedStreamer.display_name,
                            login: updatedStreamer.login,
                            profileImageUrl: updatedStreamer.profile_image_url,
                        });
                    } else {
                        statusChanges.wentOffline.push({
                            id: updatedStreamer.id,
                            name: updatedStreamer.display_name
                        });
                    }
                }
                
                // Update our local data
                this.streamers.set(updatedStreamer.id, updatedStreamer);
                
                // Update UI
                const streamerElement = document.querySelector(`.streamer-item[data-streamer-id="${updatedStreamer.id}"]`);
                if (streamerElement) {
                    // Update name with live indicator
                    const nameElement = streamerElement.querySelector('.streamer-name');
                    if (nameElement) {
                        nameElement.textContent = updatedStreamer.display_name; // Correction ici: updatedStreamer au lieu de streamer
                    }
                    
                    // Update game info
                    const gameElement = streamerElement.querySelector('.streamer-game');
                    if (gameElement) {
                        gameElement.textContent = updatedStreamer.is_live ? updatedStreamer.game_name : t('stream.waiting');
                    }

                    streamerElement.classList.toggle('live', updatedStreamer.is_live);
                    streamerElement.classList.toggle('queued', !updatedStreamer.is_live);

                    streamerElement.querySelectorAll('.player-action-btn').forEach((button) => {
                        button.disabled = !updatedStreamer.is_live;
                    });
                    
                    // Update viewer count
                    const viewersElement = streamerElement.querySelector('.streamer-viewers');
                    if (updatedStreamer.is_live) {
                        if (viewersElement) {
                            viewersElement.replaceWith(this.createViewerBadge(updatedStreamer.viewer_count));
                        } else {
                            const viewerSlot = streamerElement.querySelector('.streamer-viewer-slot');
                            if (viewerSlot) {
                                viewerSlot.append(this.createViewerBadge(updatedStreamer.viewer_count));
                            }
                        }
                    } else if (viewersElement) {
                        viewersElement.remove();
                    }
                }
            });
            
            // Handle players based on status changes
            statusChanges.wentLive.forEach(streamer => {
                this.playerManager.addPlayer(
                    streamer.id,
                    streamer.login,
                    streamer.name,
                    streamer.profileImageUrl,
                );
                this.showNotification(t('stream.live', { name: streamer.name }), 'success');
            });
            
            statusChanges.wentOffline.forEach(streamer => {
                this.playerManager.removePlayer(streamer.id);
                this.showNotification(t('stream.offline', { name: streamer.name }), 'warning');
            });

            this.syncStreamerSections();
            
            // Mettre à jour le chat global si actif
            if (this.globalChat.isGlobalMode) {
                const activeStreamers = Array.from(this.streamers.values()).filter(streamer => streamer.is_live);
                await this.globalChat.updateActiveStreamers(activeStreamers);
            }
            
            // Update URL if needed
            if (statusChanges.wentLive.length > 0 || statusChanges.wentOffline.length > 0) {
                this.updateUrl();
            }
        } catch (error) {
            console.error('Error refreshing streamer data:', error);
        }
    }

    // Update URL based on all selected streamers, including the offline queue.
    updateUrl() {
        const streamerNames = this.streamersOrder
            .map(id => this.streamers.get(id)?.login)
            .filter(Boolean);
        
        // Create new path
        let newPath = '/';
        if (streamerNames.length > 0) {
            newPath += streamerNames.join('/');
        }
        
        // Only update if different from current path
        if (window.location.pathname !== newPath) {
            history.pushState(null, null, newPath);
            
            // Notify backend about URL change
            fetch('/api/session/update-url', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ path: newPath })
            }).catch(err => console.error('Error updating session URL:', err));
        }
    }

    getCurrentStreamerLogins() {
        return this.streamersOrder
            .map(id => this.streamers.get(id)?.login)
            .filter(Boolean);
    }

    showHome() {
        this.welcomeScreen?.toggle();
    }

    // Show notification to user
    showNotification(message, type = 'info', duration = 5000) {
        // Add to queue
        this.notificationQueue.push({ message, type, duration });
        
        // Process queue if not active
        if (!this.notificationActive) {
            this.processNotificationQueue();
        }
    }
    
    processNotificationQueue() {
        if (this.notificationQueue.length === 0) {
            this.notificationActive = false;
            return;
        }
        
        this.notificationActive = true;
        
        // Get next notification
        const { message, type, duration } = this.notificationQueue.shift();
        
        // Create notification element
        const notification = document.createElement('div');
        notification.className = `custom-notification ${type}`;
        notification.textContent = message;
        
        // Add to DOM
        document.body.appendChild(notification);
        
        // Trigger animation
        setTimeout(() => {
            notification.classList.add('show');
        }, 10);
        
        // Remove after duration
        setTimeout(() => {
            notification.classList.remove('show');
            
            // Remove from DOM after animation
            setTimeout(() => {
                if (notification.parentNode) {
                    notification.parentNode.removeChild(notification);
                }
                
                // Process next notification
                this.processNotificationQueue();
            }, 300);
        }, duration);
    }

    // Add this method to the MultiStreamApp class
    isWelcomeMessageDisplayed() {
        return this.streamersList.querySelector('.welcome-message') !== null;
    }

    // Ajouter cette méthode à la classe MultiStreamApp
    showDefaultChatMessage() {
        const activeStreamers = Array.from(this.streamers.values()).filter(streamer => streamer.is_live);
        const hasLiveStreamers = activeStreamers.length > 0;
        
        // Vérifier le mode de chat actuellement sélectionné dans la sidebar
        const globalChatBtn = document.getElementById('global-chat-toggle');
        const isGlobalMode = globalChatBtn && globalChatBtn.classList.contains('active');
        
        if (hasLiveStreamers && isGlobalMode) {
            // Si des streamers sont en live et le mode global est sélectionné, activer automatiquement le chat global
            this.globalChat.enableGlobalChat(activeStreamers);
        } else {
            // Sinon, afficher le message par défaut
            this.globalChat.disableGlobalChat();
            
            let welcomeMessage;
            if (!hasLiveStreamers) {
                // Aucun streamer en live
                welcomeMessage = `
                    <div class="default-chat-message">
                        <img src="/static/images/logo.png" alt="MultiStream Logo" class="chat-logo">
                        <h3>${t('chat.welcome')}</h3>
                        <p>${t('chat.noLive')}</p>
                        <p>${t('chat.addHint')}</p>
                        <p>${t('chat.clickHint')}</p>
                    </div>
                `;
            } else {
                // Mode individuel sélectionné
                welcomeMessage = `
                    <div class="default-chat-message">
                        <img src="/static/images/logo.png" alt="MultiStream Logo" class="chat-logo">
                        <h3>${t('chat.individualMode')}</h3>
                        <p>${t('chat.clickHint')}</p>
                        <p>${t('chat.globalHint')}</p>
                    </div>
                `;
            }
            
            this.chatContainer.innerHTML = welcomeMessage;
        }
    }
    
    // Update latency button appearance based on saved settings
    updateLatencyButtonAppearance(streamerId) {
        if (!this.latencyManager) return;
        
        const latency = this.latencyManager.getLatency(streamerId);
        const streamerElement = document.querySelector(`[data-streamer-id="${streamerId}"]`);
        
        if (streamerElement) {
            const latencyBtn = streamerElement.querySelector('.latency-btn');
            if (latencyBtn) {
                if (latency > 0) {
                    latencyBtn.classList.add('has-delay');
                    latencyBtn.title = `Stream delayed by ${latency}s - Click to adjust`;
                } else {
                    latencyBtn.classList.remove('has-delay');
                    latencyBtn.title = t('stream.latency');
                }
            }
        }
    }
    
    // Update all latency button appearances
    updateAllLatencyButtonAppearances() {
        this.streamers.forEach((streamer, streamerId) => {
            this.updateLatencyButtonAppearance(streamerId);
        });
    }

    refreshTranslatedInterface() {
        if (this.streamers.size === 0) this.showWelcomeMessage();
        this.streamers.forEach((streamer, streamerId) => {
            const item = this.streamersList.querySelector(`.streamer-item[data-streamer-id="${streamerId}"]`);
            if (!item) return;
            const playerState = this.playerManager.players.get(streamerId);
            if (!streamer.is_live) item.querySelector('.streamer-game').textContent = t('stream.waiting');
            const viewers = item.querySelector('.streamer-viewers');
            if (viewers) {
                const exactCount = new Intl.NumberFormat(document.documentElement.lang).format(Number(streamer.viewer_count) || 0);
                viewers.title = t('stream.spectators', { count: exactCount });
                viewers.setAttribute('aria-label', viewers.title);
            }
            const labels = [
                ['.chat-btn', 'stream.chat'],
                ['.focus-btn', playerState?.zoomed ? 'stream.leaveFocus' : 'stream.focus'],
                ['.maximize-btn', playerState?.maximized ? 'stream.restore' : 'stream.maximize'],
                ['.latency-btn', 'stream.latency'],
                ['.remove-btn', 'common.remove'],
            ];
            labels.forEach(([selector, key]) => {
                const button = item.querySelector(selector);
                if (button) {
                    button.title = t(key);
                    button.setAttribute('aria-label', button.title);
                }
            });
        });
        this.syncStreamerSections();
        this.sidebar.updateMiniStreamers();
        this.playerManager.refreshTranslations();
        if (this.chatContainer.querySelector('.default-chat-message')) {
            this.showDefaultChatMessage();
        }
    }
}

// Initialize the application when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    const app = new MultiStreamApp();
    // Rendre l'instance accessible globalement
    window.app = app;
    
    // Handle browser back/forward navigation
    window.addEventListener('popstate', () => {
        // Just reload the page to handle navigation
        location.reload();
    });
});
